import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const pollCount = useRef(0);
  const pending = JSON.parse(localStorage.getItem("revora_pending_order") || "null");

  useEffect(() => {
    const sessionId = params.get("session_id");
    const paypalToken = params.get("token"); // PayPal returns ?token=<orderID>
    let cancelled = false;

    const pollStripe = async () => {
      while (!cancelled && pollCount.current < 20) {
        pollCount.current += 1;
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") return setState("paid");
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) setState("failed");
    };

    const capturePayPal = async () => {
      try {
        const { data } = await api.post("/payments/paypal/capture", { paypal_order_id: paypalToken });
        if (!cancelled) setState(data.payment_status === "paid" ? "paid" : "failed");
      } catch {
        if (!cancelled) setState("failed");
      }
    };

    if (sessionId) pollStripe();
    else if (paypalToken) capturePayPal();
    else setState("failed");
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-24" data-testid="order-success-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg w-full text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-volt animate-spin mx-auto mb-6" />
            <h1 className="font-display text-3xl font-black tracking-tight text-ink">Confirming your payment…</h1>
            <p className="mt-3 text-black/55 text-sm">This takes a few seconds. Don't close this tab.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-volt flex items-center justify-center mx-auto mb-8">
              <CheckCircle2 className="w-7 h-7 text-ink" />
            </div>
            <h1 className="font-display text-4xl font-black tracking-tight text-ink">Payment confirmed.</h1>
            <p className="mt-4 text-black/55 leading-relaxed">
              Your Revora order{pending ? ` for ${pending.business}` : ""} is in the queue. We'll configure your
              products to route to your review page, quality-check them, and ship. A confirmation email is on its way.
            </p>
            {pending && (
              <div className="mt-8 bg-mist border border-black/10 rounded-sm p-6">
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-black/40 mb-1">Your order number</p>
                <p className="font-display text-3xl font-black text-voltdark" data-testid="success-order-number">{pending.order_number}</p>
                <p className="text-xs text-black/45 mt-3">{pending.quantity > 1 ? `${pending.quantity} units` : "1 unit"} — ${pending.total.toFixed(2)} · {pending.email}</p>
              </div>
            )}
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {pending && (
                <Link
                  to={`/track?order=${pending.order_number}&email=${encodeURIComponent(pending.email)}`}
                  data-testid="success-track-link"
                  className="inline-flex items-center gap-2 bg-ink text-white font-bold px-7 py-3.5 rounded-full hover:bg-voltdark transition-colors duration-300"
                >
                  Track your order <ArrowRight className="w-4 h-4" />
                </Link>
              )}
              <Link to="/portal" className="inline-flex items-center gap-2 border border-black/15 font-bold px-7 py-3.5 rounded-full hover:border-ink transition-colors duration-300">
                Create your portal account
              </Link>
            </div>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-black tracking-tight text-ink">We couldn't confirm the payment</h1>
            <p className="mt-3 text-black/55 text-sm">
              If you completed payment, your order is safe — contact revorareviewsupport@gmail.com with your order number
              {pending ? ` (${pending.order_number})` : ""} and we'll confirm it manually.
            </p>
            <Link to="/" className="mt-6 inline-block bg-ink text-white font-bold px-7 py-3.5 rounded-full hover:bg-voltdark transition-colors duration-300">
              Back to site
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
